import {
  BracesIcon,
  CalendarDaysIcon,
  DollarSignIcon,
  HashIcon,
  LinkIcon,
  TextAlignStartIcon,
  ToggleRightIcon,
  type LucideIcon,
} from "lucide-react";

import { FieldDelta } from "@/components/@types";

/**
 * How a changed field the record's object file has no definition of is drawn,
 * by the model's own type for it (``FieldDelta.fieldType``): as the field type
 * a row of that kind renders, with the icon the object files give fields of
 * that kind. A reference's icon is the referenced object's, where the value
 * says what that is. Anything not here is drawn as text.
 */
export const DELTA_FIELD_KINDS: Record<
  string,
  { type: string; ReactIcon: LucideIcon }
> = {
  CharField: { type: "text", ReactIcon: TextAlignStartIcon },
  TextField: { type: "text", ReactIcon: TextAlignStartIcon },
  BooleanField: { type: "boolean", ReactIcon: ToggleRightIcon },
  IntegerField: { type: "number", ReactIcon: HashIcon },
  BigIntegerField: { type: "number", ReactIcon: HashIcon },
  SmallIntegerField: { type: "number", ReactIcon: HashIcon },
  PositiveIntegerField: { type: "number", ReactIcon: HashIcon },
  PositiveSmallIntegerField: { type: "number", ReactIcon: HashIcon },
  FloatField: { type: "number", ReactIcon: HashIcon },
  DecimalField: { type: "currency", ReactIcon: DollarSignIcon },
  DateField: { type: "date", ReactIcon: CalendarDaysIcon },
  DateTimeField: { type: "date", ReactIcon: CalendarDaysIcon },
  ForeignKey: { type: "reference", ReactIcon: LinkIcon },
  OneToOneField: { type: "reference", ReactIcon: LinkIcon },
  ManyToManyField: { type: "references", ReactIcon: LinkIcon },
  JSONField: { type: "text", ReactIcon: BracesIcon },
};

/**
 * Format a delta value for display in the tooltip
 */
export function formatDeltaValue(value: unknown): string {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if ("name" in (value as Record<string, unknown>)) {
      return String((value as Record<string, unknown>).name);
    }
    if ("identifier" in (value as Record<string, unknown>)) {
      return String((value as Record<string, unknown>).identifier).slice(0, 8);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(deepClone) as T;
  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
}

/**
 * Deep merge two objects. Source values override target values;
 * nested objects are merged recursively.
 */
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...target };
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];
      if (
        sourceValue !== null &&
        typeof sourceValue === "object" &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        typeof targetValue === "object" &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>,
        );
      } else {
        result[key] = sourceValue;
      }
    }
  }
  return result;
}

/**
 * Set a value at a nested path (e.g., "address.city" -> obj.address.city = value).
 * Deep merges if both existing and new values are objects.
 */
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  const finalKey = keys[keys.length - 1];
  const existingValue = current[finalKey];
  if (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    existingValue !== null &&
    typeof existingValue === "object" &&
    !Array.isArray(existingValue)
  ) {
    current[finalKey] = deepMerge(
      existingValue as Record<string, unknown>,
      value as Record<string, unknown>,
    );
  } else {
    current[finalKey] = value;
  }
}

/**
 * Create "before" data by applying all deltas' originalValues to the current data.
 * Reconstructs what the data looked like before the pending changes.
 *
 * For confirmed changes, pass ``resultingData`` as ``afterData`` so the diff is
 * computed against the snapshot rather than the live object state.
 *
 * FK/reference fields: if originalValue is a bare primitive (UUID string or int)
 * but the "after" field value is a reference object, wrap it as { identifier }
 * so reference FieldCells can still render something meaningful.
 */
export function createBeforeData(
  currentData: Record<string, unknown>,
  deltas: FieldDelta[],
  inputKeyToObjectKey: Record<string, string> = {},
  afterData?: Record<string, unknown>,
): Record<string, unknown> {
  const referenceData = afterData ?? currentData;
  const beforeData = deepClone(referenceData);
  for (const delta of deltas) {
    const frontendKey = inputKeyToObjectKey[delta.fieldName] ?? delta.fieldName;
    let value = delta.originalValue;
    if (
      value !== null &&
      value !== undefined &&
      (typeof value === "string" || typeof value === "number") &&
      typeof beforeData[frontendKey] === "object" &&
      beforeData[frontendKey] !== null &&
      "identifier" in (beforeData[frontendKey] as object)
    ) {
      value = { identifier: String(value) };
    }
    setNestedValue(beforeData, frontendKey, value);
  }
  return beforeData;
}
